import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { change, getFormValues } from 'redux-form'
import { useDispatch, useSelector } from 'react-redux'
// hooks
import { useStateRef } from 'hooks/useStateRef'
import { useCurrentForm } from 'hooks/risks/useCurrentForm'
// constants
import { Forms } from 'constants/forms'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// types
import { IRiskFieldValue } from 'types/risk'
import { FieldType, IField } from 'types/graphql'
// context
import { PublicContext } from 'containers/Risks/Risk/Edit/public'
import { ImportedRiskContext } from 'components/Modals/ImportedRiskModal'
// utils
import { getFormFieldValue } from 'utils/form'
import { debounce, get, isEqual } from 'lodash'

// Injury Master links are derived by the backend and never stored as a field value.
const excludedFieldTypes = [
  FieldType.Table,
  FieldType.Section,
  FieldType.InjuryMasterLinks,
  FieldType.InjuryMasterParentLinks
]

export const useSaveFieldValue = ({
  name,
  field,
  value,
  onFieldUpdate
}: {
  name: string
  field: IField
  value: IRiskFieldValue
  onFieldUpdate?: (field: IField, value: IRiskFieldValue) => void
}) => {
  const router = useRouter()
  const dispatch = useDispatch()

  const { fieldLink, linked } = useCurrentForm()

  const isPublic = React.useContext(PublicContext)

  const [isPending, setIsPending, isPendingRef] = useStateRef(false)

  const { importedRisk, isImportedField } = React.useContext(ImportedRiskContext)
  const { isRisksRegister, updateFieldValues } = React.useContext(RiskTemplateAPIContext)

  const formValues = useSelector(getFormValues(Forms.RISK_TEMPLATE_FORM))

  const prevValue = React.useRef(value?.value)
  const prevFieldValueId = React.useRef(value?.fieldValueId)

  const isRegisters = !!router.query.multiple || (isRisksRegister && !isImportedField)

  const debouncedUpdateFieldValue = React.useCallback(
    debounce(async (value: IRiskFieldValue) => {
      if (isPendingRef.current) {
        return
      }

      setIsPending(true)

      const fieldValueId = linked ? value?.linkedFieldValueId : value?.fieldValueId
      const fieldLinkId = value?.fieldLinkId || fieldLink?.id
      const linkedRiskId = value?.linkedRiskId

      const input = {
        value: getFormFieldValue(field, value?.value),
        ...(!linked && {
          ...(fieldLinkId && { fieldLinkId }),
          ...(value?.relatedFieldId && { relatedFieldId: value.relatedFieldId }),
          ...(value?.parentFieldValueId && { parentFieldValueId: value.parentFieldValueId })
        }),
        ...(fieldValueId ? { fieldValueId } : { fieldId: field.id })
      }

      const response = await updateFieldValues([input], importedRisk?.realRiskId || linkedRiskId, field)

      if (linked) {
        await updateFieldValues([
          {
            value: { value: linkedRiskId, entity: 'LINKED_RISK', linkedFieldValueId: value?.linkedFieldValueId },
            fieldLinkId,
            fieldValueId: value?.fieldValueId,
            ...(value?.relatedFieldId && { relatedFieldId: value.relatedFieldId }),
            ...(value?.parentFieldValueId && { parentFieldValueId: value.parentFieldValueId })
          }
        ])
      }

      const fieldValueUpsertId = get(
        response,
        isPublic ? 'data.fieldValueUpsertPublic[0].id' : 'data.fieldValueUpsert[0].id'
      )

      if (!fieldValueId && fieldValueUpsertId) {
        dispatch(change(Forms.RISK_TEMPLATE_FORM, `${name}.fieldValueId`, fieldValueUpsertId))
      }

      onFieldUpdate?.(field, { ...input, fieldValueId: fieldValueId || fieldValueUpsertId })

      setIsPending(false)
    }, 400),
    [name, linked, isPublic, field.id, onFieldUpdate, fieldLink?.id, importedRisk?.realRiskId]
  )

  React.useEffect(() => {
    if (!formValues || isPending || isRegisters || excludedFieldTypes.includes(field.fieldType)) {
      return
    }

    // When fieldValueId changes, the component was reused for a different row (e.g. after row deletion).
    // Reset prevValue so we don't trigger a false save due to stale ref.
    if (prevFieldValueId.current !== value?.fieldValueId) {
      prevValue.current = value?.value
      prevFieldValueId.current = value?.fieldValueId
      return
    }

    if (!isEqual(prevValue.current, value?.value)) {
      debouncedUpdateFieldValue(value)
      prevValue.current = value?.value
    }
  }, [value, isPending])

  React.useEffect(() => {
    return () => debouncedUpdateFieldValue.cancel()
  }, [])
}
